import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import {
  ApiGetSmsDetailHistoryOptions,
  ApiGetSmsListHistoryOptions,
  SmsHistory,
  SmsHistoryDetail,
  SmsHistoryDetails,
  SmsHistoryList,
  SmsScheduleType
} from '@models/reseller/sms-history-detail';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SmsHistoryService {
  private readonly baseUrl;

  data;

  constructor(private http: HttpClient) {
    this.baseUrl = environment.serverBaseUrl;
  }

  getSmsSentList(dateFrom, dateTo, recipient?, pageNum?, pageSize?, sortColumn?, sortDirection?, isRichsms?): Observable<SmsHistoryList> {
    let params = new HttpParams();
    params = params.append('dateFrom', dateFrom);
    params = params.append('dateTo', dateTo);

    if (recipient) {
      params = params.append('recipient', recipient);
    }

    if (pageNum) {
      params = params.append('page', pageNum);
    }

    if (pageSize) {
      params = params.append('size', pageSize);
    }

    if (isRichsms !== undefined) {
      params = params.append('richSms', isRichsms);
    }
    if (sortColumn) {
      switch (sortColumn) {
        case 'createTime':
          params = params.append('sortColumn', 'CREATE_TIME');

          break;
        case 'dateForSend':
          params = params.append('sortColumn', 'DATE_FOR_SEND');

          break;
        default:
          params = params.append('sortColumn', 'DATE_FOR_SEND');
          break;
      }
    }
    if (sortDirection) {
      params = params.append('sortDirection', sortDirection);
    }

    return this.http.get(this.baseUrl + 'sms/campaign/history/list-sent-sms?' + params.toString().replace(/\+/gi, '%2B')).pipe(
      map(data => {
        return SmsHistoryList.deserialize({
          data: (data && (data as any).data ? ((data as any).data as Array<string>) : []).map(data2 => SmsHistory.deserialize(data2)),
          total: (data as any).total
        });
      })
    );
  }

  getSmsSentDetails(body: ApiGetSmsDetailHistoryOptions, pageNum?, pageSize?, sortColumn?, sortDirection?): Observable<SmsHistoryDetails> {
    let params = new HttpParams();
    if (pageNum) {
      params = params.append('page', pageNum);
    }
    if (pageSize) {
      params = params.append('size', pageSize);
    }
    if (sortColumn) {
      switch (sortColumn) {
        case 'createTime':
          params = params.append('sortColumn', 'SEND_DATE');

          break;
        case 'dateForSend':
          params = params.append('sortColumn', 'DELIVERY_DATE');

          break;
        default:
          params = params.append('sortColumn', 'STATUS');
          break;
      }
    }
    if (sortDirection) {
      params = params.append('sortDirection', sortDirection);
    }
    return this.http
      .post(this.baseUrl + 'sms/campaign/history/dtls-sent-sms', body, {
        params
      })
      .pipe(
        map(data => {
          return SmsHistoryDetails.deserialize({
            data: (data && (data as any).data ? ((data as any).data as Array<string>) : []).map(data2 =>
              SmsHistoryDetail.deserialize(data2)
            ),
            total: (data as any).total
          });
        })
      );
  }

  getSingleSmsSentDetails(body: ApiGetSmsDetailHistoryOptions): Observable<SmsHistoryDetail> {
    return this.http.post<SmsHistoryDetail>(this.baseUrl + 'sms/campaign/history/single-sent-sms', body);
  }

  exportSendSmsDetail(exportFormat, idMessage, email): Observable<any> {
    let params = new HttpParams();
    params = params.append('email', email);
    const body = {
      exportFormat,
      idMessage
    };
    return this.http.post(this.baseUrl + 'sms/campaign/history/export-dtls-sent-sms', body, { params });
  }

  exportSendSmsList(exportFormat, apiGetSmsListHistoryOptions: ApiGetSmsListHistoryOptions, isAggregateByCampaign, email): Observable<any> {
    let params = new HttpParams();
    params = params.append('email', email);
    const body = {
      isAggregateByCampaign,
      exportFormat,
      dateFrom: apiGetSmsListHistoryOptions.dateFrom,
      dateTo: apiGetSmsListHistoryOptions.dateTo,
      recipient: apiGetSmsListHistoryOptions.recipient
    };
    return this.http.post(this.baseUrl + 'sms/campaign/history/export-list-sent-sms', body, { params });
  }

  getSmsScheduledList(scheduleType: SmsScheduleType, pageNum?, pageSize?, sortColumn?, sortDirection?): Observable<SmsHistoryList> {
    let params = new HttpParams();

    params = params.append('scheduleType', scheduleType.toLocaleUpperCase());

    if (pageNum) {
      params = params.append('page', pageNum);
    }

    if (pageSize) {
      params = params.append('size', pageSize);
    }

    switch (sortColumn) {
      case 'scheduleTime':
        params = params.append('sortColumn', 'SCHEDULE_TIME');

        break;
      case 'campaignName':
        params = params.append('sortColumn', 'CAMPAIGN_NAME');

        break;
      default:
        params = params.append('sortColumn', 'CAMPAIGN_NAME');
        break;
    }
    params = sortDirection ? params.append('sortDirection', sortDirection) : params.append('sortDirection', 'd');

    return this.http.get(this.baseUrl + 'sms/campaign/history/list-scheduled-messages', { params }).pipe(
      map(data => {
        return SmsHistoryList.deserialize({
          data: (data && (data as any).data ? ((data as any).data as Array<string>) : []).map(data2 => SmsHistory.deserialize(data2)),
          total: (data as any).total
        });
      })
    );
  }

  getSmsScheduledDetails(idMessage, scheduleType): Observable<SmsHistoryDetail> {
    const body = {
      idMessage,
      scheduleType
    };
    return this.http.post<SmsHistoryDetail>(this.baseUrl + 'sms/campaign/history/detail-scheduled-message', body, {});
  }

  deleteScheduledSms(idMessage, scheduleType) {
    const options = { responseType: 'text' as 'json', body: { idMessage, scheduleType } };
    return this.http.delete(this.baseUrl + 'sms/campaign/history/remove-scheduled-message', options);
  }
}
